import type { ReactNode } from "react";
import { MotionConfig, motion } from "framer-motion";
import type { LucideIcon } from "lucide-react";
import { PageHeader } from "../components/PageHeader";
import SEO from "../components/SEO";
import Silk from "@/components/reactbits/Silk";
import { PRO_FEATURES } from "../components/features/proFeatures";

const navItems = [
    { label: "For You", href: "/" },
    { label: "For Providers", href: "/healthcare" },
];

const rise = {
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.6 },
    transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
} as const;

/** One feature per screen: a short heading over its live screen, together fitting one desktop viewport. */
function Feature({ id, icon: Icon, badge, title, text, children }: { id: string; icon: LucideIcon; badge: string; title: string; text: string; children: ReactNode }) {
    return (
        <section aria-labelledby={id} className="mx-auto flex min-h-[100svh] max-w-6xl flex-col justify-center pb-10 pt-24">
            <div className="mx-auto mb-6 max-w-2xl text-center md:mb-8">
                <motion.span
                    {...rise}
                    className="inline-flex items-center gap-2 rounded-full border border-purple-500/30 bg-purple-500/10 px-3.5 py-1 text-sm font-medium text-purple-700 dark:text-purple-300"
                >
                    <Icon className="size-4" />
                    {badge}
                </motion.span>
                <motion.h2 {...rise} id={id} className="mt-3 text-balance text-3xl font-semibold tracking-tight text-slate-900 md:text-4xl dark:text-white">
                    {title}
                </motion.h2>
                <motion.p {...rise} className="mt-2 text-balance text-base text-slate-600 dark:text-slate-400">
                    {text}
                </motion.p>
            </div>
            {children}
        </section>
    );
}

/** Work in progress: Quilt Pro features, each shown as a live, touchable screen. Unlisted for now. */
export default function FeaturesPage() {
    return (
        <MotionConfig reducedMotion="user">
            <SEO title="Features | Quilt Pro" description="A look inside Quilt Pro, the platform clinics use to run Quilt for their patients." path="/features" noindex />
            <PageHeader navItems={navItems} />

            {/* The same silk as the patients page hero, held behind the whole page */}
            <div aria-hidden className="pointer-events-none fixed inset-0 z-0 opacity-40 dark:opacity-30">
                <Silk speed={10} scale={1} darkColor="#5e3a7d" lightColor="#d8b4fe" noiseIntensity={1.2} rotation={0} />
            </div>

            <div className="relative z-[1] overflow-x-clip px-4 sm:px-6">
                {PRO_FEATURES.map(({ id, icon, badge, title, text, Mock }) => (
                    <Feature key={id} id={`feature-${id}`} icon={icon} badge={badge} title={title} text={text}>
                        <Mock />
                    </Feature>
                ))}
            </div>
        </MotionConfig>
    );
}
